import { ApiEndpoint, Config, ICmTableFieldDef, MapLayerConfig } from "../types";
import { ProvisionConfigPayload, ProvisionExternalDataSource } from "./legacyConfigImport";
import { MEDIA_LAYER_ID, MEDIA_MARKER_ICON_KEY, withGeotaggedPhotosLayer } from "./mediaLayer";
import {
  boundaryStyleToGeostyler,
  geostylerToBoundaryStyle,
  isDefaultBoundaryStyle,
  ORG_BOUNDARY_SOURCE,
  withBoundaryGeostyler,
  type BoundaryStyleValue
} from "./boundaryStyle";
import { hiddenEndpointSlugs, type EndpointOverrides } from "./endpointVisibility";

// -----------------------------------------------------------------------------
// TEMPORARY ANTI-CORRUPTION BRIDGE
// -----------------------------------------------------------------------------
// This module is a *temporary* boundary that normalises the `GET /api/v2/config/`
// response into the legacy `Config` shape the app already consumes. The agreed
// target (see the config-migration PR review) is for the backend to converge on
// the legacy `Config` shape directly, at which point this adapter collapses to a
// pass-through and can be deleted. Until then it is the single reconciliation
// point for remote config, and the boundary below is concretely typed so a
// backend rename fails at compile time instead of silently yielding `undefined`.
// -----------------------------------------------------------------------------

/** Config schema major version this client knows how to read. */
export const SUPPORTED_CONFIG_MAJOR = 1;

/** A nested `apiEndpoints[]` entry from `GET /api/v2/config/` (snake_case). */
export interface RemoteApiEndpoint {
  slug: string;
  name: string;
  description?: string;
  spatial_type?: ApiEndpoint["spatialType"];
  field_schema?: ICmTableFieldDef[];
  map_display_config?: { icon?: string; color?: string; style?: Record<string, any>; style_key?: string };
  extras?: Record<string, any>;
  api_path?: string | null;
  geojson_path?: string | null;
  /** The published CmForm bound to this table (snake_case, as served). */
  form?: {
    form_group_id: string;
    uuid: string;
    version: number;
    status: string;
    name: string;
    has_unpublished_draft?: boolean;
  } | null;
}

/** A Fulcrum entry from `externalDataSources[]`. */
export interface RemoteExternalDataSource {
  name: string;
  formId: string;
  tableName: string;
  description?: string;
  dataSourceType?: string;
  dataSourceConfig?: ApiEndpoint["dataSourceConfig"];
  dateConfig?: ApiEndpoint["dateConfig"];
}

/** A `homePageShortcuts[]` entry (snake_case). */
export interface RemoteHomePageShortcut {
  name: string;
  target_tab: number;
  table_slug?: string;
}

/** UI flags served under the `uiConfig` catch-all wrapper. */
export interface RemoteUiConfig {
  sidebarOpen?: boolean;
  primaryFilterLayer?: string;
  defaultDataTable?: string;
  showMediaInRelatedItemsCloud?: boolean;
  showGeoInRelatedItemsCloud?: boolean;
  alwaysRequestRelatedItemsAndMedia?: boolean;
  mediaUpload?: Config["mediaUpload"];
  enableZoomToRelatedItems?: boolean;
  hideCardsWithoutImages?: boolean;
  limitDataDrawerToExtent?: boolean;
  enableServerSearch?: boolean;
  [key: string]: any;
}

/**
 * Concrete response shape of `GET /api/v2/config/` (database-driven config).
 *
 * Top-level keys are camelCase (`appTitle`, `authConfig`, `uiConfig`, ...), while the
 * nested `apiEndpoints[]` entries use snake_case (`slug`, `api_path`, `spatial_type`,
 * `field_schema`). This adapter normalises everything back into the legacy {@link Config}
 * shape that the existing components/hooks still consume, so no consumer changes are
 * required during the migration.
 */
export interface RemoteConfigResponse {
  schema_version: string;
  /**
   * CDN host serving this environment's media (e.g. `cdn-dev.clientx.culturemap.app`).
   * Used by the admin console to flag branding URLs pointing at another environment.
   */
  mediaHost?: string;
  appTitle: string;
  logoPath: string;
  loginImagePath: string;
  loginMessage: string;
  colorScheme: Config["colorScheme"];
  homePageTitle: string;
  homePageDescription: string;
  homePageShortcuts?: RemoteHomePageShortcut[];
  initialMapView: Config["initialMapView"];
  mapLayers?: Array<MapLayerConfig & { source?: string }>;
  apiEndpoints?: RemoteApiEndpoint[];
  dashboardUrls?: Config["dashboardUrls"];
  authConfig?: Config["auth"];
  uiConfig?: RemoteUiConfig;
  /**
   * Per-endpoint overrides keyed by slug: `name`, `description`, and `hidden`
   * (see `endpointVisibility.ts`). Hidden endpoints are dropped from the adapted
   * config, so the app never sees them; the admin console reads this raw response
   * and still lists them so they can be turned back on.
   */
  apiEndpointOverrides?: EndpointOverrides;
  externalDataSources?: RemoteExternalDataSource[];
  tabularData?: Config["tabularData"];
}

/** Extracts the major version from a `schema_version` string (e.g. "1.0" -> 1). */
const parseSchemaMajor = (version: unknown): number | null => {
  if (typeof version !== "string") return null;
  const major = parseInt(version.split(".")[0], 10);
  return Number.isNaN(major) ? null : major;
};

/**
 * Detects the backend config by its `schema_version` marker AND branches on the
 * major version: an unknown/newer major is rejected (returns false) so the caller
 * falls back to a known-good config instead of mis-parsing a shape it doesn't
 * understand.
 */
export const isRemoteConfig = (raw: any): raw is RemoteConfigResponse => {
  if (!raw || typeof raw !== "object" || typeof raw.schema_version !== "string") {
    return false;
  }
  const major = parseSchemaMajor(raw.schema_version);
  if (major === null) {
    console.warn(`[configAdapter] Unrecognised schema_version "${raw.schema_version}" — ignoring remote config.`);
    return false;
  }
  if (major !== SUPPORTED_CONFIG_MAJOR) {
    console.warn(
      `[configAdapter] Unsupported config schema_version "${raw.schema_version}" ` +
        `(this client supports major ${SUPPORTED_CONFIG_MAJOR}). Ignoring remote config to avoid mis-parsing.`
    );
    return false;
  }
  return true;
};

/** Legacy `api_endpoint` values are relative (`api/v2/...`) with no leading slash. */
const stripLeadingSlash = (value?: string | null): string => (value ? value.replace(/^\/+/, "") : "");

/** Unique tableName for the synthesized "Tabular data" tab (dataset_type='tabular'). */
export const TABULAR_DATASETS_TABLE = "spatial-datasets-tabular";

/** tableName the app addresses the Spatial Dataset warehouse by, whatever the backend spells its slug. */
export const SPATIAL_DATASETS_TABLE = "spatial-datasets";

/** The warehouse endpoint arrives as `spatialdataset` or `spatial-datasets` depending on backend version. */
export const isSpatialDatasetSlug = (slug: string): boolean => slug === "spatialdataset" || slug === SPATIAL_DATASETS_TABLE;

/**
 * Runtime `tableName` for a raw endpoint slug. Endpoints are addressed by `tableName`
 * everywhere in the app (`?endpoint=`, home-page shortcut targets, layer lookups) and
 * the spatial dataset warehouse is the one endpoint whose slug differs from it — so
 * anything writing an endpoint reference into config goes through here.
 */
export const endpointTableName = (slug: string): string => (isSpatialDatasetSlug(slug) ? SPATIAL_DATASETS_TABLE : slug);

/**
 * System endpoint the backend does not (yet) include in the GET response but which
 * dozens of components look up by the hardcoded `tableName` "restrictions".
 */
const RESTRICTIONS_ENDPOINT: ApiEndpoint = {
  name: "Restrictions",
  tableName: "restrictions",
  relationshipKey: "restrictions",
  queryParam: "restrictions",
  api_endpoint: "api/v2/restrictions/",
  description: "Restrictions is a collection of all the access control groups in the database"
};

const inferEntityType = (slug: string, spatialType?: string): ApiEndpoint["entityType"] => {
  if (slug === "media") return "media";
  if (isSpatialDatasetSlug(slug)) return "spatial-dataset";
  return spatialType && spatialType !== "none" ? "geo" : "table";
};

/** Maps a raw `apiEndpoints[]` entry (CM table or system) to the legacy {@link ApiEndpoint}. */
const adaptEndpoint = (ep: RemoteApiEndpoint): ApiEndpoint => {
  const slug: string = ep.slug;
  const spatialType = ep.spatial_type;
  const isMedia = slug === "media";
  const isSpatialDataset = isSpatialDatasetSlug(slug);
  const isSystem = isMedia || isSpatialDataset;

  // System endpoints keep their legacy tableName; CM tables use the slug verbatim.
  const tableName = endpointTableName(slug);

  const endpoint: ApiEndpoint = {
    name: ep.name,
    tableName,
    relationshipKey: tableName,
    tableSlug: slug,
    api_endpoint: stripLeadingSlash(ep.api_path),
    description: ep.description || "",
    entityType: inferEntityType(slug, spatialType),
    spatialType,
    fields: ep.field_schema || []
  };

  // A form-bound table captures through its CmForm's schema (the entity form then
  // renders the form's own fields instead of its generic Name/Description inputs).
  if (ep.form?.uuid) {
    endpoint.form = {
      formGroupId: ep.form.form_group_id,
      uuid: ep.form.uuid,
      version: ep.form.version,
      status: ep.form.status,
      name: ep.form.name,
      hasUnpublishedDraft: ep.form.has_unpublished_draft
    };
  }

  if (!isSystem) {
    // CM-table map layers are generated at runtime by useCmTableMapLayers with the
    // id `cm-table-{slug}`; link the endpoint to that layer id.
    endpoint.dataSourceType = "cm-table";
    endpoint.mapLayerId = `cm-table-${slug}`;
    if (ep.map_display_config?.style) {
      endpoint.style = ep.map_display_config.style;
    }
    if (ep.map_display_config?.style_key) {
      endpoint.styleKey = ep.map_display_config.style_key;
    }
  }

  if (isMedia) {
    endpoint.queryParam = "media";
    endpoint.mapLayerId = MEDIA_LAYER_ID;
    endpoint.sortBy = "id";
    endpoint.sortDirection = "desc";
  } else if (isSpatialDataset) {
    endpoint.sortBy = "id";
    endpoint.sortDirection = "desc";
  }

  return endpoint;
};

/** Converts an `externalDataSources[]` (Fulcrum) entry into a legacy {@link ApiEndpoint}. */
const adaptFulcrumSource = (src: RemoteExternalDataSource): ApiEndpoint => ({
  name: src.name,
  tableName: src.tableName,
  relationshipKey: src.tableName,
  formId: src.formId,
  dataSourceType: "fulcrum",
  // Fulcrum map layers use the source name as their layer id.
  mapLayerId: src.name,
  entityType: "geo",
  api_endpoint: "",
  description: src.description || "",
  dataSourceConfig: src.dataSourceConfig,
  dateConfig: src.dateConfig
});

const adaptHomePageShortcuts = (shortcuts: RemoteHomePageShortcut[] = []): Config["homePageShortcuts"] =>
  shortcuts.map((s) => ({
    name: s.name,
    targetTab: s.target_tab,
    options: { tableName: s.table_slug || "" }
  }));

/**
 * Normalises a `GET /api/v2/config/` response into the legacy {@link Config} shape.
 */
export const adaptRemoteConfig = (raw: RemoteConfigResponse): Config => {
  const ui = raw.uiConfig || {};

  // An endpoint marked `hidden` in `api_endpoint_overrides` is dropped here, at the
  // single point every app consumer reads — the sidebar, the data-drawer grid, the
  // form builder's "existing table" picker and the map all render from
  // `config.apiEndpoints`, and its `cm-table-{slug}` layer is built per endpoint, so
  // removing it once gates all of them including the layers panel.
  //
  // **Currently this filter matches nothing.** `GET /api/v2/config/` is the merged
  // config: the backend applies the overrides itself and omits a hidden endpoint
  // from `apiEndpoints` before we ever see it. Kept deliberately, for two reasons —
  // it is the correct client-side semantics if the backend ever serves hidden
  // endpoints with a marker instead of dropping them (which is the better contract,
  // since dropping them leaves the admin console with no metadata for a hidden
  // table — see `ConfigEditorPage`'s reconstruction from the overrides map), and it
  // costs one Set lookup per endpoint.
  const hidden = new Set(hiddenEndpointSlugs(raw.apiEndpointOverrides));
  const visibleRemoteEndpoints = (raw.apiEndpoints || []).filter((ep) => !hidden.has((ep as any)?.slug));

  const cmEndpoints: ApiEndpoint[] = visibleRemoteEndpoints.map(adaptEndpoint);
  const fulcrumEndpoints: ApiEndpoint[] = (raw.externalDataSources || []).map(adaptFulcrumSource);
  const apiEndpoints = [...cmEndpoints, ...fulcrumEndpoints];

  if (!apiEndpoints.some((e) => e.tableName === "restrictions")) {
    apiEndpoints.push(RESTRICTIONS_ENDPOINT);
  }

  // Tabular (CSV → Parquet / Power BI) datasets are SpatialDatasets with
  // dataset_type='tabular'. The backend excludes them from the default
  // /spatial-datasets/ list, so they get their own tab that queries
  // ?dataset_type=tabular (from tabularData.listEndpoint). We reuse the
  // spatial-dataset entityType so the existing list/detail machinery applies;
  // tabular-specific UI is gated on dataset_type inside SpatialDatasetDetail.
  const tabular = raw.tabularData;
  if (tabular?.listEndpoint && !apiEndpoints.some((e) => e.tableName === TABULAR_DATASETS_TABLE)) {
    apiEndpoints.push({
      name: tabular.name || "Tabular data",
      tableName: TABULAR_DATASETS_TABLE,
      relationshipKey: TABULAR_DATASETS_TABLE,
      tableSlug: TABULAR_DATASETS_TABLE,
      api_endpoint: stripLeadingSlash(tabular.listEndpoint),
      description: "Tabular datasets (CSV → Parquet) available for Power BI and download",
      entityType: "spatial-dataset",
      sortBy: "id",
      sortDirection: "desc"
    });
  }

  // CM-table map layers are generated at runtime by useCmTableMapLayers from the
  // apiEndpoints (id: `cm-table-{slug}`). Drop the backend's `source: "cmtable"`
  // entries so we don't render two layers per table.
  const authoredLayers = (raw.mapLayers || [])
    .filter((layer) => layer.source !== "cmtable")
    // The derived org-boundary layer arrives as a flat MapLibre paint dict (the
    // backend's `boundary_style`, merged over its defaults). Attaching a GeoStyler
    // style routes it through the shared converter, which is what lets a filled
    // boundary keep a full-width outline — a single MapLibre fill layer's only
    // outline is `fill-outline-color`, locked at 1px. See `boundaryStyle.ts`.
    .map((layer) => (layer.source === ORG_BOUNDARY_SOURCE ? withBoundaryGeostyler(layer) : layer)) as MapLayerConfig[];

  // Geotagged Photos is mandatory, not opt-in: legacy customers had it authored
  // in their config.json, but a provisioned org's config arrives without it, so
  // uploaded geotagged media rendered nowhere and the layer never appeared in the
  // panel. Synthesised here when absent; an authored entry with the same id wins.
  const mapLayers = withGeotaggedPhotosLayer(authoredLayers, ui[MEDIA_MARKER_ICON_KEY] as string | undefined);

  return {
    schema_version: raw.schema_version,
    appTitle: raw.appTitle,
    logoPath: raw.logoPath,
    loginImagePath: raw.loginImagePath,
    loginMessage: raw.loginMessage,
    colorScheme: raw.colorScheme,
    homePageTitle: raw.homePageTitle,
    homePageDescription: raw.homePageDescription,
    homePageShortcuts: adaptHomePageShortcuts(raw.homePageShortcuts),
    initialMapView: raw.initialMapView,
    mapLayers,
    apiEndpoints,
    dashboardUrls: raw.dashboardUrls,
    auth: raw.authConfig,

    // Lift uiConfig flags to the root level where legacy consumers read them.
    sidebarOpen: ui.sidebarOpen ?? false,
    primaryFilterLayer: ui.primaryFilterLayer ?? "",
    defaultDataTable: ui.defaultDataTable,
    showMediaInRelatedItemsCloud: ui.showMediaInRelatedItemsCloud,
    showGeoInRelatedItemsCloud: ui.showGeoInRelatedItemsCloud,
    alwaysRequestRelatedItemsAndMedia: ui.alwaysRequestRelatedItemsAndMedia,
    mediaUpload: ui.mediaUpload,
    enableZoomToRelatedItems: ui.enableZoomToRelatedItems,
    hideCardsWithoutImages: ui.hideCardsWithoutImages,
    limitDataDrawerToExtent: ui.limitDataDrawerToExtent,
    enableServerSearch: ui.enableServerSearch,
    tabularData: raw.tabularData
  };
};

/**
 * Inverse of {@link adaptRemoteConfig} for the admin console: normalises a
 * `GET /api/v2/config/` response back into the {@link ProvisionConfigPayload}
 * (snake_case) that `PATCH /api/v2/config/` accepts. This is what lets the
 * existing-org config editor round-trip *every* writable section — not just the
 * branding scalars — so an operator edits the live config rather than defaults.
 *
 * Derived layers are excluded so the editor never persists them as stored
 * external layers: `source === "cmtable"` (regenerated from CmTables at runtime)
 * and `source === "org-boundary"` (derived from Organisation.geometry, served
 * from the boundary endpoint). Object/array sections default to `{}`/`[]` so the
 * section editors always have a concrete value to bind to.
 *
 * The boundary layer is the one derived layer that carries a *writable* section:
 * its appearance lives in `OrganisationConfig.boundary_style`. It is lifted back
 * out here — without it the boundary style editor would have nothing to bind to,
 * and a save from any other tab would be the only thing keeping the field alive
 * by omission.
 */
export const remoteConfigToPayload = (raw: RemoteConfigResponse): ProvisionConfigPayload => {
  const externalMapLayers = (raw.mapLayers || []).filter(
    (layer) => layer.source !== "cmtable" && layer.source !== ORG_BOUNDARY_SOURCE
  ) as Record<string, any>[];

  // The derived layer is the only source. `boundary_style` is applied ONTO that
  // layer and never emitted as its own section, confirmed by Prabin 15 Sep — an
  // earlier read of a top-level `boundaryStyle` / `boundary_style` key was
  // speculative and has been removed rather than left as dead cover.
  const boundaryLayer = (raw.mapLayers || []).find((layer) => layer.source === ORG_BOUNDARY_SOURCE) as BoundaryStyleValue | undefined;
  // Omitted entirely for an unstyled org: the GET returns the *merged* layer, so
  // writing it back unconditionally would convert "never styled" into "pinned to
  // today's defaults" on the first save from any tab. Omission is also what keeps
  // a stored style safe when the layer is absent — the save is a PATCH, so a key
  // we don't send is a key the backend leaves alone.
  const boundaryStyle = isDefaultBoundaryStyle(boundaryLayer)
    ? undefined
    : geostylerToBoundaryStyle(boundaryStyleToGeostyler(boundaryLayer), boundaryLayer?.initialVisibility);

  const externalDataSources: ProvisionExternalDataSource[] = (raw.externalDataSources || []).map((src) => ({
    name: src.name,
    formId: src.formId,
    tableName: src.tableName,
    description: src.description,
    dataSourceType: src.dataSourceType,
    dataSourceConfig: src.dataSourceConfig,
    dateConfig: src.dateConfig
  }));

  const homePageShortcuts = (raw.homePageShortcuts || []).map((s) => ({
    name: s.name,
    target_tab: s.target_tab,
    table_slug: s.table_slug
  }));

  return {
    app_title: raw.appTitle ?? "",
    logo_path: raw.logoPath ?? "",
    login_image_path: raw.loginImagePath ?? "",
    login_message: raw.loginMessage ?? "",
    color_primary: raw.colorScheme?.primary ?? "",
    color_secondary: raw.colorScheme?.secondary ?? "",
    home_page_title: raw.homePageTitle ?? "",
    home_page_description: raw.homePageDescription ?? "",
    home_page_shortcuts: homePageShortcuts,
    initial_map_view: {
      longitude: raw.initialMapView?.longitude,
      latitude: raw.initialMapView?.latitude,
      zoom: raw.initialMapView?.zoom,
      pitch: raw.initialMapView?.pitch
    },
    external_map_layers: externalMapLayers,
    ...(boundaryStyle ? { boundary_style: boundaryStyle } : {}),
    external_data_sources: externalDataSources,
    dashboard_urls: (raw.dashboardUrls as Array<Record<string, any>>) || [],
    ui_config: (raw.uiConfig as Record<string, any>) || {},
    auth_config: (raw.authConfig as Record<string, any>) || {},
    // Carried through so editing anything else in the config editor round-trips
    // the overrides (names, descriptions, hidden flags) rather than omitting them.
    api_endpoint_overrides: (raw.apiEndpointOverrides as Record<string, any>) || {}
  };
};
